import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Clock, Check, Phone, HeartHandshake, Sunrise, School, CalendarDays } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import CalEmbed from "@/components/CalEmbed";
import { CAL_LINKS } from "@/lib/calLinks";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CONSULTATION_TYPES, BOOKING_FAQS } from "@/data/content";

const ICONS = { Phone, HeartHandshake, Sunrise, School };

const EXPECTATIONS = [
  { title: "A conversation, not an evaluation", text: "We listen first. You will leave with clarity, whether or not we work together." },
  { title: "30–60 minutes of focused time", text: "Discovery calls run 15 minutes; full consultations run up to an hour. Your time is respected." },
  { title: "A written summary after we meet", text: "Every consultation ends with notes, initial recommendations, and clear next steps." },
];

export default function Book() {
  const [selected, setSelected] = useState(CONSULTATION_TYPES[0].id);
  const schedulerRef = useRef(null);
  const active = CONSULTATION_TYPES.find((c) => c.id === selected);
  const ActiveIcon = ICONS[active.icon];

  return (
    <>
      <section className="container-site pb-14 pt-16 sm:pt-24" data-testid="book-hero">
        <p className="overline-label">Book now</p>
        <h1 className="mt-5 max-w-3xl font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.1}>Let's start with</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-terracotta-500">a conversation.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Consultations are for families, caregivers, professionals, and organizations —
            anyone carrying a behavior question that deserves a thoughtful answer. Sessions
            run 30–60 minutes over secure video, scheduled around your life.
          </p>
        </Reveal>
      </section>

      <section className="container-site pb-20" data-testid="book-expectations">
        <div className="grid gap-5 md:grid-cols-3">
          {EXPECTATIONS.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.08}>
              <div className="card-lift h-full rounded-3xl border border-border bg-card p-7 shadow-soft" data-testid={`book-expectation-${i + 1}`}>
                <Clock className="h-5 w-5 text-terracotta-500" />
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground">{e.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{e.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-card py-20" data-testid="book-choose-section">
        <div className="container-site">
          <Reveal>
            <SectionHeader
              overline="Step one"
              title="Choose your consultation"
              description="Select the session that fits. You can always change it before confirming."
            />
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CONSULTATION_TYPES.map((c, i) => {
              const Icon = ICONS[c.icon];
              const isActive = selected === c.id;
              return (
                <Reveal key={c.id} delay={i * 0.06}>
                  <button
                    type="button"
                    data-testid={`consultation-option-${c.id}`}
                    aria-pressed={isActive}
                    onClick={() => {
                      setSelected(c.id);
                      schedulerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className={`card-lift group flex h-full w-full flex-col rounded-3xl border p-7 text-left transition-colors duration-300 ${
                      isActive
                        ? "border-terracotta-500 bg-terracotta-500/5 shadow-lift dark:bg-terracotta-500/10"
                        : "border-border bg-background shadow-soft"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-2xl transition-colors duration-300 ${
                        isActive ? "bg-terracotta-500 text-cream" : "bg-forest/10 text-forest dark:bg-sage/10 dark:text-sage"
                      }`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      {isActive && (
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-terracotta-500 text-cream" data-testid={`consultation-selected-${c.id}`}>
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </div>
                    <h3 className="mt-5 font-heading text-base font-semibold text-foreground">{c.title}</h3>
                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.16em] text-sage-600 dark:text-sage">
                      {c.duration} · {c.price}
                    </p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
                    <span
                      data-testid={`consultation-schedule-${c.id}`}
                      className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                        isActive
                          ? "bg-terracotta-500 text-cream"
                          : "bg-forest text-cream group-hover:bg-terracotta-500 dark:bg-terracotta-500 dark:group-hover:bg-terracotta-400"
                      }`}
                    >
                      <CalendarDays className="h-4 w-4" /> Schedule This Service
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container-site py-20" data-testid="book-scheduler-section">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <SectionHeader
              overline="Step two"
              title="Pick a time that works"
              description={`You're booking: ${active.title}`}
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div ref={schedulerRef} className="mt-10 scroll-mt-28" data-testid="booking-scheduler">
              <CalEmbed
                key={selected}
                namespace={CAL_LINKS[selected].namespace}
                calLink={CAL_LINKS[selected].calLink}
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-center text-sm text-muted-foreground">
              Questions before booking?{" "}
              <Link to="/contact" data-testid="book-contact-link" className="font-medium text-forest underline-offset-4 transition-colors duration-300 hover:text-terracotta-500 hover:underline dark:text-sage dark:hover:text-terracotta-400">
                Send us a message
              </Link>{" "}
              — we reply within one to two business days.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-card py-20" data-testid="book-faq-section">
        <div className="container-site">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <SectionHeader overline="Before you book" title="Booking questions" align="center" />
            </Reveal>
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="mt-10 w-full" data-testid="book-faq-accordion">
                {BOOKING_FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-border">
                    <AccordionTrigger
                      data-testid={`book-faq-trigger-${i + 1}`}
                      className="text-left font-heading text-base font-medium text-foreground hover:text-terracotta-500 hover:no-underline"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-12 flex items-center justify-center gap-3 rounded-3xl bg-forest p-6 text-cream dark:bg-forest-800">
                <CalendarClock className="h-5 w-5 shrink-0 text-terracotta-400" />
                <p className="text-sm">
                  Free 15-minute discovery calls are always available — no commitment, no pressure.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
